import React, { useState, useEffect, useCallback } from "react";
import "jspdf-autotable";
import formatDate, {
  formatMonthDate,
  errorAlert,
  successAlert,
} from "../../utils/services";
import {
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Typography,
  Box,
  Tooltip,
  IconButton,
  TableBody,
  TextField,
} from "@mui/material";
import axiosInstance from "../../helpers/axios";
import NoDataFound from "../../components/NoDataFound";
import { ToastContainer } from "react-toastify";
import TableSkeletonLoader from "../../components/ui/TableSkeletonLoader";
import CustomTooltip from "../../components/ui/CustomTootltip";
import CustomDateRangePicker from "../../components/ui/CustomDateRangePicker";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";

const TimespentTracker = () => {
  // --- date filters ---
  const [startSelectedDate, setStartSelectedDate] = useState(() => {
    const today = new Date();
    const pastDate = new Date(today.setDate(today.getDate() - 7));
    return formatDate(pastDate);
  });
  const [endSelectedDate, setEndSelectedDate] = useState(
    formatDate(new Date())
  );

  const [uniqueMatterNames, setUniqueMatterNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // --- per-matter edit state ---
  const [enteredNames, setEnteredNames] = useState({});
  const [enteredRemarks, setEnteredRemarks] = useState({});
  const [enteredPeriod, setEnteredPeriod] = useState({
    // [matterId]: { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }
  });
  const [editingMatter, setEditingMatter] = useState({});

  // field changes
  const handleFieldChange = (matterId, field, value) => {
    if (field === "enteredBy") {
      setEnteredNames((prev) => ({ ...prev, [matterId]: value }));
    } else if (field === "remarks") {
      setEnteredRemarks((prev) => ({ ...prev, [matterId]: value }));
    } else if (field === "periodStart" || field === "periodEnd") {
      setEnteredPeriod((prev) => ({
        ...prev,
        [matterId]: {
          ...prev[matterId],
          [field === "periodStart" ? "start" : "end"]: value,
        },
      }));
    }
  };

  // enter edit mode: seed inputs
  const handleEditMatter = (matterId) => {
    const m = uniqueMatterNames.find((m) => m.id === matterId) || {};
    setEditingMatter((p) => ({ ...p, [matterId]: true }));
    setEnteredNames((p) => ({ ...p, [matterId]: m.enteredBy || "" }));
    setEnteredRemarks((p) => ({ ...p, [matterId]: m.remarks || "" }));
    setEnteredPeriod((p) => ({
      ...p,
      [matterId]:
        m.periodStart && m.periodEnd
          ? {
              start: m.periodStart.split("T")[0],
              end: m.periodEnd.split("T")[0],
            }
          : { start: "", end: "" },
    }));
  };

  // save changes
  const handleSaveMatter = async (matterId) => {
    try {
      const enteredBy = enteredNames[matterId]?.trim();
      const remarks = enteredRemarks[matterId]?.trim() || "";
      const { start, end } = enteredPeriod[matterId] || {};

      if (!start || !end) {
        return errorAlert("Please select both start and end dates.");
      }
      if (!enteredBy) {
        return errorAlert("Please enter your name before saving.");
      }

      await axiosInstance.put("/updateLastSentTSPeriod", {
        id: matterId,
        start,
        end,
        enteredBy,
        remarks,
      });
      successAlert("Matter updated successfully!");

      // update local list
      setUniqueMatterNames((prev) =>
        prev.map((m) =>
          m.id === matterId
            ? {
                ...m,
                periodStart: new Date(start).toISOString(),
                periodEnd: new Date(end).toISOString(),
                enteredBy,
                remarks,
              }
            : m
        )
      );
      setEditingMatter((p) => ({ ...p, [matterId]: false }));
      await filterBetweenDates();
    } catch (error) {
      console.error("Error updating matter:", error);
      errorAlert("Failed to update matter.");
    }
  };

  // cancel edit
  const handleCancelEdit = (matterId) => {
    const m = uniqueMatterNames.find((m) => m.id === matterId) || {};
    setEnteredNames((p) => ({ ...p, [matterId]: m.enteredBy || "" }));
    setEnteredRemarks((p) => ({ ...p, [matterId]: m.remarks || "" }));
    setEnteredPeriod((p) => ({
      ...p,
      [matterId]:
        m.periodStart && m.periodEnd
          ? {
              start: m.periodStart.split("T")[0],
              end: m.periodEnd.split("T")[0],
            }
          : { start: "", end: "" },
    }));
    setEditingMatter((p) => ({ ...p, [matterId]: false }));
  };

  // fetch data
  const filterBetweenDates = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axiosInstance.get(
        "/workHour/getTimespentTrackerData",
        { params: { fromDate: startSelectedDate, toDate: endSelectedDate } }
      );
      setUniqueMatterNames(resp.data.data);
    } catch {
      errorAlert("Error fetching timespent tracker data!");
    } finally {
      setLoading(false);
    }
  }, [startSelectedDate, endSelectedDate]);

  // date‐range picker
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: "",
    end: "",
  });
  const handleDateRangeChange = (start, end) => {
    setSelectedDateRange({ start, end });
    setStartSelectedDate(start);
    setEndSelectedDate(end);
  };

  useEffect(() => {
    if (startSelectedDate && endSelectedDate) filterBetweenDates();
  }, [startSelectedDate, endSelectedDate, filterBetweenDates]);

  useEffect(() => {
    const timeoutId = setTimeout(
      () => setDebouncedSearchQuery(searchQuery),
      500
    );

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const filteredMatterNames = uniqueMatterNames.filter((matter) => {
    const normalizedQuery = debouncedSearchQuery.trim().toLowerCase();
    if (!normalizedQuery) return true;

    return [matter.name, matter.enteredBy, matter.remarks].some((value) =>
      value?.toLowerCase().includes(normalizedQuery)
    );
  });

  return (
    <>
      <ToastContainer />

      <Paper elevation={0} className="rounded-full">
        {/* header & date picker */}
        <Grid container alignItems="center" spacing={1} mb={2}>
          <Grid
            item
            xs={12}
            md={4}
            display="flex"
            alignItems="center"
            gap={0.5}
          >
            <Typography variant="h5">Timespent Tracker</Typography>
            <Tooltip title="*Displays matters where time was entered in the selected period.">
              <IconButton size="small">
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search matters, entered by or remarks"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            display="flex"
            alignItems="center"
            justifyContent={{ md: "flex-end" }}
            gap={1}
          >
            <CustomTooltip text="Select Dates">
              <CustomDateRangePicker
                startDate={selectedDateRange.start}
                endDate={selectedDateRange.end}
                onDateRangeChange={handleDateRangeChange}
                iconSize={30}
              />
            </CustomTooltip>
            <Box display="flex" gap={1}>
              <Typography>{formatMonthDate(startSelectedDate)}</Typography>
              <Typography>-</Typography>
              <Typography>{formatMonthDate(endSelectedDate)}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "75vh" }}>
          <Box display="flex" justifyContent="space-between" px={2} py={1}>
            <Typography variant="body2">
              MATTERWISE TIMESPENT TRACKER ({formatMonthDate(startSelectedDate)}{" "}
              - {formatMonthDate(endSelectedDate)})
            </Typography>
            <Typography variant="body2">
              Total Matters: {filteredMatterNames.length}
            </Typography>
          </Box>

          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Sr No.</TableCell>
                <TableCell>Matter</TableCell>
                <TableCell>Last Entry Date</TableCell>
                <TableCell>Last Sent TS Period</TableCell>
                <TableCell>Entered by</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell>Latest TS</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeletonLoader rows={6} colSpan={8} />
              ) : filteredMatterNames.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <NoDataFound />
                  </TableCell>
                </TableRow>
              ) : (
                filteredMatterNames.map((matter, idx) => {
                  const isEditing = !!editingMatter[matter.id];
                  const hasPeriod = !!matter.periodStart && !!matter.periodEnd;
                  return (
                    <TableRow key={matter.id}>
                      <TableCell>{idx + 1}</TableCell>

                      <TableCell
                        sx={{
                          color: hasPeriod ? "inherit" : "red",
                          fontWeight: hasPeriod ? "normal" : "bold",
                        }}
                      >
                        {matter.name}
                      </TableCell>

                      <TableCell>
                        {matter.lastEntryDate
                          ? formatMonthDate(matter.lastEntryDate)
                          : "-"}
                      </TableCell>

                      <TableCell>
                        {isEditing ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <CustomDateRangePicker
                              startDate={enteredPeriod[matter.id]?.start || ""}
                              endDate={enteredPeriod[matter.id]?.end || ""}
                              onDateRangeChange={(start, end) => {
                                handleFieldChange(
                                  matter.id,
                                  "periodStart",
                                  start
                                );
                                handleFieldChange(matter.id, "periodEnd", end);
                              }}
                              iconSize={20}
                            />
                            <Typography variant="body2">
                              {enteredPeriod[matter.id]?.start &&
                              enteredPeriod[matter.id]?.end
                                ? `${formatMonthDate(
                                    enteredPeriod[matter.id].start
                                  )} – ${formatMonthDate(
                                    enteredPeriod[matter.id].end
                                  )}`
                                : "Select range"}
                            </Typography>
                          </Box>
                        ) : hasPeriod ? (
                          <Typography>
                            {`${formatMonthDate(
                              matter.periodStart
                            )} – ${formatMonthDate(matter.periodEnd)}`}
                          </Typography>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      <TableCell>
                        {isEditing ? (
                          <TextField
                            size="small"
                            value={enteredNames[matter.id] || ""}
                            onChange={(e) =>
                              handleFieldChange(
                                matter.id,
                                "enteredBy",
                                e.target.value
                              )
                            }
                          />
                        ) : (
                          matter.enteredBy || "-"
                        )}
                      </TableCell>

                      <TableCell style={{ textAlign: "left" }}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            value={enteredRemarks[matter.id] || ""}
                            onChange={(e) =>
                              handleFieldChange(
                                matter.id,
                                "remarks",
                                e.target.value
                              )
                            }
                          />
                        ) : (
                          matter.remarks || "-"
                        )}
                      </TableCell>

                      <TableCell style={{ textAlign: "left" }}>
                        {matter.lastSentTSReportLink ? (
                          <IconButton
                            size="small"
                            component="a"
                            href={matter.lastSentTSReportLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      <TableCell>
                        {isEditing ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleSaveMatter(matter.id)}
                              disabled={
                                !enteredNames[matter.id]?.trim() ||
                                !enteredPeriod[matter.id]?.start ||
                                !enteredPeriod[matter.id]?.end
                              }
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleCancelEdit(matter.id)}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => handleEditMatter(matter.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

export default TimespentTracker;
